import {
  FC, memo, useCallback, useState,
} from 'react';
import { App, Upload, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useAppSelector } from 'app/providers/Store';
import { userSelectors } from 'entities/user';
import { attachmentApi } from 'entities/attachment/api/attachmentApi';
import { useGlobalSpinner } from 'shared/hooks';
import { AttachmentDownloadList, Title } from 'shared/ui';
import styles from './EngineReportSection.module.scss';

interface EngineReportSectionProps {
  engineId: number | null;
  reportUuids?: string[];
  /** Перечитать карточку двигателя, чтобы подхватить новый отчёт. */
  onUploaded: () => void;
}

const ENGINE_PARENT_TYPE = 'engine';
const REPORT_TYPE = 'report';

/**
 * Отчёты по результатам инспекций. Загружать может только администратор
 * (на бэке /attachments/upload закрыт ролью ADMIN), скачивать - все, кто видит машину.
 */
export const EngineReportSection: FC<EngineReportSectionProps> = memo(({
  engineId,
  reportUuids,
  onUploaded,
}) => {
  const { message } = App.useApp();
  const { showSpinner, hideSpinner } = useGlobalSpinner();
  const hasAdminRole = useAppSelector(userSelectors.hasAdminRole);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = useCallback(async (file: File) => {
    if (!engineId) return false;

    try {
      setIsUploading(true);
      showSpinner('Загрузка отчёта...');
      await attachmentApi.upload(engineId, ENGINE_PARENT_TYPE, REPORT_TYPE, file);
      message.success('Отчёт загружен');
      onUploaded();
    } catch (error) {
      console.error('Ошибка загрузки отчёта:', error);
      message.error('Ошибка загрузки отчёта');
    } finally {
      setIsUploading(false);
      hideSpinner();
    }

    return false;
  }, [engineId, onUploaded, showSpinner, hideSpinner, message]);

  return (
    <div className={styles.reportSection}>
      <div className={styles.header}>
        <Title level={5}>Отчёты по инспекциям</Title>
        {hasAdminRole && (
          <Upload accept=".pdf" showUploadList={false} beforeUpload={handleUpload}>
            <Button icon={<UploadOutlined />} loading={isUploading}>
              Загрузить отчёт (PDF)
            </Button>
          </Upload>
        )}
      </div>

      <AttachmentDownloadList
        attachmentUuids={reportUuids}
        label=""
        emptyText="Отчёты не загружены"
      />
    </div>
  );
});

EngineReportSection.displayName = 'EngineReportSection';
